<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { reactive, ref } from 'vue';
import { array, object } from 'yup';

const {
    fromOptions,
    toOptions,
    from,
    to = [],
    subject = '',
    body = '',
    callback,
} = defineProps({
    fromOptions: {
        type: Array,
        required: true,
    },
    toOptions: {
        type: Array,
        required: true,
    },
    from: {
        type: Object,
        required: false,
    },
    to: {
        type: Array,
        required: false,
    },
    subject: {
        type: String,
        required: false,
    },
    body: {
        type: String,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const form = reactive({
    from: from ? from : fromOptions[0],
    to: to,
    subject: subject,
    body: body,
});

const validationSchema = object({
    from: object().required('Ce champ est obligatoire.'),
    to: array().min(1).required('Ce champ est obligatoire.'),
});

const isOpen = ref(false);

const loading = ref(false);

function setLoading(value) {
    loading.value = value;
}

function setIsOpen(value) {
    isOpen.value = value;
}

function sendEmail() {
    loading.value = true;

    callback(
        form.from,
        form.to,
        form.subject,
        form.body,
        setLoading,
        setIsOpen,
    );
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="almost-full">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Envoyer un email </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="sendEmail"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.from"
                                field-id="from"
                                field-name="from"
                                label="De"
                                :options="fromOptions"
                            >
                                <template #buttonContent>
                                    <template v-if="form.from">
                                        {{ form.from.name }}
                                    </template>

                                    <template v-else> &nbsp; </template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }} - {{ option.email }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.to"
                                field-id="to"
                                field-name="to"
                                label="À"
                                :options="toOptions"
                                multiple
                            >
                                <template #buttonContent>
                                    <template v-if="form.to.length > 0">
                                        {{
                                            form.to
                                                .map((to) => to.name)
                                                .join(', ')
                                        }}
                                    </template>

                                    <template v-else> &nbsp; </template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }} - {{ option.email }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <InputField
                            v-model="form.subject"
                            label="Sujet"
                            field-name="subject"
                            field-id="subject"
                        />

                        <EditorField
                            v-model="form.body"
                            label="Corps du message"
                            field-name="body"
                            field-id="body"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="loading"
                    :errors="errors"
                    without-back-button
                    submit-text="Envoyer"
                    submitting-text="Envoi en cours..."
                >
                    <!-- Given the email as it stands, e.g. to send a test of it first. -->
                    <template v-if="$slots['footer-actions']" #actions>
                        <slot
                            name="footer-actions"
                            :from="form.from"
                            :subject="form.subject"
                            :body="form.body"
                        />
                    </template>
                </FormFooter>
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
